import React from "react";
import { Link } from "react-router-dom";
import { FaBalanceScale, FaGavel, FaMapMarkerAlt } from "react-icons/fa";
import { resolveHubAuctionImage } from "../../utils/b2bHubHomeMedia";
import {
  buildAuctionSpecItems,
  canShowAuctionBidNow,
  getAuctionDetailHref,
  getAuctionImagePath,
  getAuctionLocation,
  getAuctionShareImage,
  getAuctionStartingPriceLabel,
  resolveAuctionRibbon,
} from "../../utils/auctionCardHelpers";
import MarketplaceProductShareButton from "./MarketplaceProductShareButton";

export default function AuctionListingGridCard({ auction, onToggleCompare, compared = false }) {
  const detailHref = getAuctionDetailHref(auction);
  const imagePath = getAuctionImagePath(auction);
  const location = getAuctionLocation(auction);
  const priceLabel = getAuctionStartingPriceLabel(auction);
  const specItems = buildAuctionSpecItems(auction);
  const ribbon = resolveAuctionRibbon(auction);
  const sellerName = auction.seller_company || auction.seller_name || "Auction Seller";
  const showBidNow = canShowAuctionBidNow(auction);

  return (
    <article className="marketplace-product-grid-card marketplace-product-grid-card--auction">
      <div className="marketplace-product-grid-card__media">
        {ribbon ? (
          <span className={`marketplace-product-grid-card__ribbon ${ribbon.className}`}>{ribbon.label}</span>
        ) : null}
        <Link to={detailHref} className="marketplace-product-grid-card__media-link">
          {imagePath ? (
            <img
              src={resolveHubAuctionImage(imagePath, auction.auction_id)}
              alt={auction.product_name}
              loading="lazy"
            />
          ) : (
            <div className="marketplace-product-grid-card__no-image">No image</div>
          )}
        </Link>
      </div>

      <div className="marketplace-product-grid-card__body">
        <Link to={detailHref} className="marketplace-product-grid-card__title">
          {auction.product_name}
        </Link>

        <div className="marketplace-product-grid-card__meta">
          <span className="marketplace-product-grid-card__company">{sellerName}</span>
          {location ? (
            <span className="marketplace-product-grid-card__location">
              <FaMapMarkerAlt aria-hidden /> {location}
            </span>
          ) : null}
        </div>

        <div className="marketplace-product-grid-card__price-row">
          {priceLabel !== "—" ? <p className="marketplace-product-grid-card__price">{priceLabel}</p> : null}
        </div>

        {specItems.length ? (
          <dl className="marketplace-product-grid-card__specs">
            {specItems.map((spec, index) => (
              <div
                key={spec.empty ? `empty-${index}` : `${spec.label}-${spec.value}`}
                className={`marketplace-product-grid-card__spec${
                  spec.empty ? " marketplace-product-grid-card__spec--empty" : ""
                }`}
                aria-hidden={spec.empty ? true : undefined}
              >
                {!spec.empty ? (
                  <>
                    <dt>{spec.label}</dt>
                    <dd>{spec.value}</dd>
                  </>
                ) : null}
              </div>
            ))}
          </dl>
        ) : null}
      </div>

      <div className="marketplace-product-grid-card__actions">
        <div className="marketplace-product-grid-card__actions-primary">
          {showBidNow ? (
            <Link to={detailHref} className="marketplace-product-grid-card__btn marketplace-product-grid-card__btn--primary">
              <FaGavel aria-hidden /> Bid Now
            </Link>
          ) : (
            <Link to={detailHref} className="marketplace-product-grid-card__btn marketplace-product-grid-card__btn--primary">
              View Auction
            </Link>
          )}
          <Link
            to={detailHref}
            className="marketplace-product-grid-card__btn marketplace-product-grid-card__btn--outline-dark"
          >
            View Details
          </Link>
        </div>
        <div className="marketplace-product-grid-card__actions-secondary">
          <button
            type="button"
            className={`marketplace-product-grid-card__btn marketplace-product-grid-card__btn--ghost${compared ? " is-active" : ""}`}
            onClick={() => onToggleCompare?.(auction.auction_id)}
          >
            <FaBalanceScale aria-hidden /> Compare
          </button>
          <MarketplaceProductShareButton
            productId={auction.auction_id}
            title={auction.product_name}
            sharePath={detailHref}
            imageUrl={getAuctionShareImage(auction)}
            companyName={sellerName}
            description={auction.product_description || `Live auction listing${location ? ` in ${location}` : ""}`}
            className="marketplace-product-grid-card__share"
            triggerClassName="marketplace-product-grid-card__btn marketplace-product-grid-card__btn--ghost marketplace-product-share__trigger"
          />
        </div>
      </div>
    </article>
  );
}
